import React, { useState, useEffect } from "react";
import { FaBullhorn, FaClipboardList, FaBars, FaCog, FaMailBulk, FaUser, FaChartLine, FaCogs, FaTasks, FaCalendarCheck } from "react-icons/fa";
import { RiAdminFill } from "react-icons/ri";
import { SiAdobeaudition, SiPivotaltracker } from "react-icons/si";
import { FiChevronRight } from "react-icons/fi";
import { IoBuild } from "react-icons/io5";
import { MdEmail, MdChecklist } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import { AiOutlineFileSearch } from "react-icons/ai";
import { fetchPermissions } from "./permissionsService";
import "../css/main/Sidebar.css";
import { SidebarProps, PermissionTabs, MenuItem } from '../interFaces/interfaces';
import { apiCall } from "./ApiCall";
import EncryptionUtils from "./encryptionUtils";
const Sidebar: React.FC<SidebarProps & { isMobile?: boolean }> = ({ isSidebarOpen, toggleSidebar, onSidebarToggle, isMobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [sidebarHover, setSidebarHover] = useState(false);
  const [permissions, setPermissions] = useState<PermissionTabs | null>(null);
  const [theme, setTheme] = useState<string>("light");
  const [isLandscape, setIsLandscape] = useState(false);
  const userInfo = EncryptionUtils.get<any>("userInfo", null);
  const userId = userInfo?.id;
  useEffect(() => {
    const checkOrientation = () => {
      const landscape = isMobile && window.innerWidth > window.innerHeight;
      setIsLandscape(landscape);
    };
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, [isMobile]);
  useEffect(() => {
    if (onSidebarToggle) {
      onSidebarToggle(isSidebarOpen);
    }
  }, [isSidebarOpen, onSidebarToggle]);
  const updateCSSVariables = (theme: Record<string, string>) => {
    for (const [key, value] of Object.entries(theme)) {
      const cssVariable = `--custom-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
      document.documentElement.style.setProperty(cssVariable, value);
    }
  };
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "light";
    setTheme(savedTheme);
    document.documentElement.setAttribute("data-theme", savedTheme);
    if (savedTheme === "custom" && userId) {
      const loadCustomTheme = async () => {
        try {
          const data = await apiCall("get", `/api/themes/${userId}`);
          if (data?.theme) {
            localStorage.setItem("userTheme", JSON.stringify(data.theme));
            updateCSSVariables(data.theme);
          }
        } catch (err) {
          console.error("Error loading custom theme:", err);
        }
      };
      loadCustomTheme();
    }
  }, [userId]);
  const toggleTheme = () => {
    let newTheme: string;
    if (theme === "light") newTheme = "dark";
    else if (theme === "dark") newTheme = "custom";
    else newTheme = "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
    if (newTheme === "custom") {
      const savedUserTheme = localStorage.getItem("userTheme");
      if (savedUserTheme) {
        updateCSSVariables(JSON.parse(savedUserTheme));
      }
    }
  };
  const getThemeIcon = () => {
    switch (theme) {
      case "light": return "🌞";
      case "dark": return "🌙";
      case "custom": return "🎨";
      default: return "🌞";
    }
  };
  useEffect(() => {
    fetchPermissions().then((data) => {
      if (data) setPermissions(data.tabs);
    });
  }, []);
  const handleMenuItemClick = (itemAction: () => void) => {
    itemAction();
    if (isMobile && isSidebarOpen) toggleSidebar();
  };
  const menuItemTemplates: MenuItem[] = [
    { title: "Role Mapping", icon: <FaClipboardList />, action: () => navigate("/rolemapping"), path: "/rolemapping", permissionKey: "roleMapping" },
    { title: "User Management", icon: <FaUser />, action: () => navigate("/usermanagement"), path: "/usermanagement", permissionKey: "userManagement" },
    { title: "Email Template", icon: <MdEmail />, action: () => navigate("/templates"), path: "/templates", permissionKey: "emailTemplate" },
    { title: "Field Mapping", icon: <FaCog />, action: () => navigate("/initialsetup"), path: "/initialsetup", permissionKey: "initialSetup" },
    { title: "Project Data Upload", icon: <FaMailBulk />, action: () => navigate("/bulkupload"), path: "/bulkupload", permissionKey: "bulkUpload" },
    { title: "Build Formula", icon: <IoBuild />, action: () => navigate("/buildformula"), path: "/buildformula", permissionKey: "buildFormula" },
    { title: "Projects", icon: <FaClipboardList />, action: () => navigate("/project"), path: "/project", permissionKey: "projects" },
    { title: "Campaigns", icon: <FaBullhorn />, action: () => navigate("/campaigns"), path: "/campaigns", permissionKey: "campaigns" },
    { title: "Functions", icon: <FaCogs />, action: () => navigate("/functions"), path: "/functions", permissionKey: "functions" },
    { title: "Function Campaigns", icon: <FaChartLine />, action: () => navigate("/functioncampaigns"), path: "/functioncampaigns", permissionKey: "functionCampaigns" },
    { title: "Checklist Management", icon: <RiAdminFill />, action: () => navigate("/adminchecklists"), path: "/adminchecklists", permissionKey: "checkListManagement" },
    { title: "Function Checklist Management", icon: <FaTasks />, action: () => navigate("/functionchecklist"), path: "/functionchecklist", permissionKey: "functionCheckListManagement" },
    { title: "Audit Execution", icon: <SiAdobeaudition />, action: () => navigate("/auditorchecklists"), path: "/auditorchecklists", permissionKey: "checkListExecution" },
    { title: "Function Audit Execution", icon: <MdChecklist />, action: () => navigate("/auditorfunctionchecklists"), path: "/auditorfunctionchecklists", permissionKey: "funtionCheckListExecution" },
    { title: "NC Tracker", icon: <SiPivotaltracker />, action: () => navigate("/nctracker"), path: "/nctracker", permissionKey: "NCTracker" },
    { title: "Function NC Tracker", icon: <AiOutlineFileSearch />, action: () => navigate("/functionnctracker"), path: "/functionnctracker", permissionKey: "functionNcTracker" },
    { title: "Meeting Tracker", icon: <FaCalendarCheck />, action: () => navigate("/meetingtracker"), path: "/meetingtracker", permissionKey: "meetingTracker" },
    { title: "Function Meeting Tracker", icon: <FaCalendarCheck />, action: () => navigate("/functionmeetingtracker"), path: "/functionmeetingtracker", permissionKey: "functionMeetingTracker" }
  ];
  const mappedMenuItems: MenuItem[] = menuItemTemplates.map((item) => ({ ...item, isActive: location.pathname === item.path, visible: permissions ? permissions[item.permissionKey]?.visible : false, }))
    .filter((item) => item.visible);
  const shouldShowSidebar = isMobile ? isSidebarOpen : true;
  return (
    <>
      {shouldShowSidebar && (
        <div className={`sidebar ${isSidebarOpen || sidebarHover ? "open" : ""} ${isLandscape ? 'landscape' : ''} ${isMobile ? 'mobile' : ''}`} onMouseEnter={() => !isMobile && setSidebarHover(true)} onMouseLeave={() => !isMobile && setSidebarHover(false)}>
          <div className="sidebar-logo">
            {(isSidebarOpen || sidebarHover) && (
              <span className="sidebar-logo-text">
                <img src="/auditzenlogo-sidebar.png" alt="Audit Illustration" className="sidebar-logo-text" />
              </span>
            )}
            {(isSidebarOpen || sidebarHover) && (
              <button onClick={toggleTheme} className="theme-toggle-btn">{getThemeIcon()}</button>
            )}
            <button className="sidebar-toggle-btn" onClick={toggleSidebar}><FaBars /></button>
          </div>
          <div className="sidebar-menu">
            {mappedMenuItems.map((item, index) => (
              <div key={index}
                className={`sidebar-menu-item ${hoverIndex === index ? "hover" : ""} ${item.isActive ? "active" : ""}`}
                onClick={() => handleMenuItemClick(item.action)}
                onMouseEnter={() => !isMobile && setHoverIndex(index)}
                onMouseLeave={() => !isMobile && setHoverIndex(null)}>
                <div className="sidebar-menu-icon">{item.icon}</div>
                {(isSidebarOpen || sidebarHover) && (
                  <>
                    <span className="sidebar-menu-title">{item.title}</span><FiChevronRight />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
export default Sidebar;